import { startBeacon } from "@kensio/rainlytics/beacon";
import { reportVitals } from "@kensio/rainlytics/beacon/vitals";

import { startArticleWriting } from "./article-writing.js";
import { startAudioControls } from "./audio-controls.js";
import { startBiaoqingbao } from "./biaoqingbao.js";
import { startDownloadOffer } from "./download-offer.js";
import { startFuxiSettingsBar } from "./fuxi-settings-bar.js";
import { startFuxiSettingsUnsaved } from "./fuxi-settings-unsaved.js";
import { startFuxiZone } from "./fuxi-zone.js";
import { startHtmxRebinding } from "./htmx-rebinding.js";
import { startLijuCopyButtons } from "./liju-copy.js";
import { startSaveLiju } from "./save-liju.js";
import { startNavMenus } from "./nav-menus.js";
import { carryOfferPlacement } from "./pro-page-placement.js";
import { startProUsage } from "./pro-usage.js";
import { startSaveWords } from "./save-word.js";
import { savedWordLatencyReporter } from "./save-word-latency.js";
import { startSignInLink } from "./sign-in-link.js";
import { startShareFriendlies } from "./share-friendly.js";
import { startLookupHistory } from "./lookup-history.js";
import { startVideoPlayer } from "./video-player.js";

/**
 * The script every page loads.
 *
 * **Bootstrap's JavaScript is gone from it.** The navbar collapse and the tools
 * dropdown were the only two things on this site that Bootstrap's JavaScript
 * did, and both are native `<details>` elements now: the browser opens them,
 * and there is nothing left to import. The dropdown was the expensive half,
 * because it is what pulled in Popper to position the menu.
 *
 * That took the bundle from 46,981 bytes to a little over a thousand. It also
 * fixed the defect the imports were there to avoid: below Bootstrap's `lg`
 * breakpoint the bar collapsed to a button that only its JavaScript could open,
 * so a visitor on a phone whose script had not arrived had no way to reach any
 * section of the site from any page on it.
 *
 * Bootstrap's stylesheet is still here. `assets/sass/_vendor.scss` no longer
 * takes its `nav`, `navbar` or `dropdown` partials, those three having had no
 * markup to style once this file changed, but the reboot, the grid, the buttons
 * and the form controls remain and are what the next phase is about.
 *
 * **The 例句 copy buttons.** The one piece of this site's own behaviour in here,
 * and it is here rather than in a bundle of its own because it is a few hundred
 * bytes: a second request on the sentence pages would cost more than carrying it
 * everywhere does. It finds nothing to do on the other thousand pages.
 *
 * **The share-friendly buttons.** Share and Copy link under the card at the
 * foot of a 词典 entry or a 例句, shown where the browser can do them. A few
 * hundred bytes on the same argument as the copy buttons, finding nothing to
 * do on a page whose card is not drawn yet. See `share-friendly.ts`.
 *
 * **The navigation menus.** Both are native `<details>` and open with no script
 * at all, which is the whole point of them; what this adds is dismissal. A
 * disclosure closes when its own summary is used and at no other time, so
 * without this a reader who opens the tools menu has to find the word "Tools"
 * again to be rid of it. See `nav-menus.ts`.
 *
 * **The sign-in link.** The bar is rendered once for the whole site and served
 * from the edge, so the markup cannot say which page a reader is on. This puts
 * that page on the link, and the callback ends there instead of on the home
 * page. See `sign-in-link.ts`.
 *
 * **The writing an article's prose is drawn in.** A reader who chose 繁體 was
 * getting it in the 例句 and nowhere else, because those are the only Chinese
 * on the site that was ever written more than one way. This redraws the rest
 * from a table the page carries. It is the largest thing in here and still
 * under a kilobyte, and it returns on its first line for the reader who chose
 * 简体 and on every page that is not an article. See `article-writing.ts`.
 *
 * **The vocabulary download gate.** It turns the two HSK list downloads off for
 * a signed-out reader and puts a line offering an account under them, and it is
 * in here on the same argument as the copy buttons. A few hundred bytes that
 * find nothing to do on every page but the thirteen 词典 level listings. The
 * addresses stay on the links and the files stay in the bucket, so this asks a
 * reader in a browser and stops nobody else. See `download-offer.ts`.
 *
 * **The save control.** A 词典 entry carries a button that keeps the word, and
 * the page is served from the edge, so it cannot say whether the reader already
 * holds it. This asks, relabels the ones they do, and takes the press where it
 * stands. Nothing on the control needs it: with the script blocked the form
 * posts and the reader lands on their list. See `save-word.ts`.
 *
 * **Binding the save controls again after an htmx swap.** `/user/ci/` and
 * `/user/liju/` replace part of themselves rather than fetching the whole page,
 * and the controls above are inside what gets replaced. The listener costs
 * every other page nothing, since the event it waits for is only ever
 * dispatched by a library those pages do not load. See `htmx-rebinding.ts`.
 *
 * **The time zone 复习 offers a reader.** The settings page counts a day on a
 * zone the reader chooses out of four hundred names, and this is what offers
 * them the one their browser is in. It is here rather than in the 复习 bundle
 * because the form it sits in is a region htmx replaces, and the rebinding
 * above is what calls it again. See `fuxi-zone.ts`.
 *
 * **A session's own three went to a bundle of their own.** The keys, the
 * marking sounds and the tone numbers under a reading field are at
 * `/user/fuxi/` and nowhere else, and the third of them carries part of
 * `@kensio/pinyinjs`. See `src/site/main-fuxi.ts`.
 *
 * **The 词典 audio controls.** A pilot hundred of the entries carry a recording
 * of the headword in two voices, and this shows the controls and plays them.
 * It returns on its first line for every page carrying none, which today is
 * every page but those hundred. A signed-out reader's press offers an account
 * rather than playing. See `audio-controls.ts` and `docs/audio.md`.
 *
 * **The offer that brought a reader to `/pro/`.** The page is static, so its
 * button to the account page is written naming the page itself, and this puts
 * the placement the reader arrived with on it instead. It returns on its first
 * line for a page with no `from`. See `pro-page-placement.ts`.
 *
 * **The analytics beacon.** The site's CloudFront access logs already record
 * every request, and they cannot record how long a page took to settle or how
 * much it moved while settling. They also stop at the response boundary, so
 * they cannot measure a saved-word request as the browser experiences it. The
 * beacon reports Core Web Vitals and saved-word request latency to the
 * collection path `BeaconPath` puts on the distribution. That path answers
 * 204 at the edge and writes the request into the same log. It is on the site's
 * own domain, so there is no second host or extra connection.
 * `lib/site/distribution.ts` has the other half.
 *
 * **Route changes are off, because this site has none.** Every page here is a
 * document the browser fetched, so the beacon's route watching would wrap the
 * History API and never fire. The one `replaceState` on the site is in
 * `src/tools`, a different bundle, and it moves the query string rather than
 * the path. Turn `reportRoutes` back on the day a page navigates without a
 * request.
 *
 * The page this loads on is not reported as a view either. CloudFront already
 * recorded the request that fetched it, and reporting it again would count one
 * view twice.
 *
 * No `DOMContentLoaded` around any of them. The tag in
 * `page-sections/header-start.html` is `type="module"`, which defers, so the
 * document is parsed by the time this runs.
 */
const beacon = startBeacon({ reportRoutes: false });

startLijuCopyButtons(document);
startShareFriendlies(document);
startBiaoqingbao(document);
startNavMenus(document);
startSignInLink(document, window.location.pathname);
carryOfferPlacement(document, window.location.search);
startArticleWriting(document);
startDownloadOffer(document);
startVideoPlayer(document);
const savedWordOptions = {
  requestStarted: savedWordLatencyReporter({
    beacon,
    clock: performance,
    page: window.location.pathname,
  }),
};

startSaveWords(document, globalThis.fetch, savedWordOptions);
startSaveLiju(document);
startHtmxRebinding(document, savedWordOptions);
startFuxiZone(document);
startFuxiSettingsUnsaved(document);
startFuxiSettingsBar(document);

startAudioControls(document, window.location.pathname);

reportVitals(beacon);

startLookupHistory(document);
void startProUsage({ document, fetch: globalThis.fetch.bind(globalThis) });
